import { PlanetFilterInput } from '@apeiron/planet'
import { styled } from '@mui/material'
import AttackSVG from '@public/icons/icon-stats-attack.svg'
import AttackRangeSVG from '@public/icons/icon-stats-attack-range.svg'
import AttackSpeedSVG from '@public/icons/icon-stats-attack-speed.svg'
import CriticaChanceSVG from '@public/icons/icon-stats-critical-chance.svg'
import CriticalDamageSVG from '@public/icons/icon-stats-critical-damage.svg'
import DefenseSVG from '@public/icons/icon-stats-defense.svg'
import HealthSVG from '@public/icons/icon-stats-health.svg'
import IntelligenceSVG from '@public/icons/icon-stats-intelligence.svg'
import BlurForm from '@src/components/share/filter/BlurForm'
import {
  FilterSection,
  FilterSectionContent,
  FilterSectionHeader,
  FilterSectionTitle
} from '@src/components/share/filter/FilterSection'
import RangeSlider from '@src/components/share/filter/RangeSlider'
import ResetFilter from '@src/components/share/filter/ResetFilter'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

const Content = styled(FilterSectionContent)`
  &.MuiAccordionDetails-root {
    padding-left: 24px;
    padding-right: 10px;
    padding-top: 8px;
    padding-bottom: 14px;
    overflow: hidden;
  }
`

const Form = styled(BlurForm)`
  > * + * {
    margin-top: 16px;
  }
`

export const StatsFilterSection: React.FC<StatsFilterSectionProps> = props => {
  const { className, filterOptions, onFilterOptionsChange } = props

  const { t } = useTranslation()

  const numFilters =
    (filterOptions.healthMin != null || filterOptions.healthMax != null
      ? 1
      : 0) +
    (filterOptions.energyMin != null || filterOptions.energyMax != null
      ? 1
      : 0) +
    (filterOptions.atkPowerMin != null || filterOptions.atkPowerMax != null
      ? 1
      : 0) +
    (filterOptions.atkSpeedMin != null || filterOptions.atkSpeedMax != null
      ? 1
      : 0) +
    (filterOptions.critDmgMin != null || filterOptions.critDmgMax != null
      ? 1
      : 0) +
    (filterOptions.critChanceMin != null || filterOptions.critChanceMax != null
      ? 1
      : 0) +
    (filterOptions.atkRangeMin != null || filterOptions.atkRangeMax != null
      ? 1
      : 0) +
    (filterOptions.armorMin != null || filterOptions.armorMax != null ? 1 : 0)

  const onReset = useCallback(() => {
    onFilterOptionsChange({
      ...filterOptions,
      healthMin: undefined,
      energyMin: undefined,
      atkPowerMin: undefined,
      atkSpeedMin: undefined,
      critDmgMin: undefined,
      critChanceMin: undefined,
      atkRangeMin: undefined,
      armorMin: undefined,
      healthMax: undefined,
      energyMax: undefined,
      atkPowerMax: undefined,
      atkSpeedMax: undefined,
      critDmgMax: undefined,
      critChanceMax: undefined,
      atkRangeMax: undefined,
      armorMax: undefined
    })
  }, [filterOptions, onFilterOptionsChange])

  return (
    <FilterSection className={className} defaultExpanded>
      <FilterSectionHeader>
        <FilterSectionTitle>
          {t('filter.sections.stats.title')}
        </FilterSectionTitle>
        <ResetFilter filterCount={numFilters} onReset={onReset} />
      </FilterSectionHeader>
      <Content>
        <Form>
          <RangeSlider
            labelText={t('filter.sections.stats.health')}
            labelIconURL={HealthSVG.src}
            min={0}
            max={999}
            minValueKey='healthMin'
            maxValueKey='healthMax'
            filterOptions={filterOptions}
            onFilterOptionsChange={onFilterOptionsChange}
          />
          <RangeSlider
            labelText={t('filter.sections.stats.energy')}
            labelIconURL={IntelligenceSVG.src}
            min={0}
            max={99}
            minValueKey='energyMin'
            maxValueKey='energyMax'
            filterOptions={filterOptions}
            onFilterOptionsChange={onFilterOptionsChange}
          />
          <RangeSlider
            labelText={t('filter.sections.stats.attack_power')}
            labelIconURL={AttackSVG.src}
            min={0}
            max={99}
            minValueKey='atkPowerMin'
            maxValueKey='atkPowerMax'
            filterOptions={filterOptions}
            onFilterOptionsChange={onFilterOptionsChange}
          />
          <RangeSlider
            labelText={t('filter.sections.stats.attack_speed')}
            labelIconURL={AttackSpeedSVG.src}
            min={0}
            max={99}
            minValueKey='atkSpeedMin'
            maxValueKey='atkSpeedMax'
            filterOptions={filterOptions}
            onFilterOptionsChange={onFilterOptionsChange}
          />
          <RangeSlider
            labelText={t('filter.sections.stats.critical_strike')}
            labelIconURL={CriticalDamageSVG.src}
            min={0}
            max={99}
            minValueKey='critDmgMin'
            maxValueKey='critDmgMax'
            filterOptions={filterOptions}
            onFilterOptionsChange={onFilterOptionsChange}
          />
          <RangeSlider
            labelText={t('filter.sections.stats.critical_chance')}
            labelIconURL={CriticaChanceSVG.src}
            min={0}
            max={99}
            minValueKey='critChanceMin'
            maxValueKey='critChanceMax'
            filterOptions={filterOptions}
            onFilterOptionsChange={onFilterOptionsChange}
          />
          <RangeSlider
            labelText={t('filter.sections.stats.attack_range')}
            labelIconURL={AttackRangeSVG.src}
            min={0}
            max={99}
            minValueKey='atkRangeMin'
            maxValueKey='atkRangeMax'
            filterOptions={filterOptions}
            onFilterOptionsChange={onFilterOptionsChange}
          />
          <RangeSlider
            labelText={t('filter.sections.stats.armor')}
            labelIconURL={DefenseSVG.src}
            min={0}
            max={99}
            minValueKey='armorMin'
            maxValueKey='armorMax'
            filterOptions={filterOptions}
            onFilterOptionsChange={onFilterOptionsChange}
          />
        </Form>
      </Content>
    </FilterSection>
  )
}

export type StatsFilterSectionProps = {
  className?: string
  filterOptions: PlanetFilterInput
  onFilterOptionsChange: (input: PlanetFilterInput) => void
}
